import { ComponentProps, ReactNode } from 'react';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { IconFont } from '@/components/iconfont';
import styles from './tooltip.module.scss';
import { classNames } from '@/utils/ui/classname';

export interface TooltipButtonProps extends ComponentProps<typeof BaseTooltip.Trigger> {
  tooltip?: ReactNode;
  side?: ComponentProps<typeof BaseTooltip.Positioner>['side'];
  align?: ComponentProps<typeof BaseTooltip.Positioner>['align'];
  sideOffset?: ComponentProps<typeof BaseTooltip.Positioner>['sideOffset'];
  alignOffset?: ComponentProps<typeof BaseTooltip.Positioner>['alignOffset'];
  arrow?: boolean;
  popupClassName?: string;
  /** 定位锚点，缺省即触发器自身。可以是元素、ref，或自带 getBoundingClientRect 的虚拟元素 */
  anchor?: ComponentProps<typeof BaseTooltip.Positioner>['anchor'];
  /** tooltip 开关回调（含悬浮 popup 时仍为开），转发到 BaseTooltip.Root */
  onOpenChange?: ComponentProps<typeof BaseTooltip.Root>['onOpenChange'];
}

export function TooltipButton({
  tooltip,
  side,
  align,
  sideOffset,
  alignOffset,
  arrow,
  popupClassName,
  anchor,
  onOpenChange,
  ...props
}: TooltipButtonProps) {
  return (
    <BaseTooltip.Root disabled={!tooltip} onOpenChange={onOpenChange}>
      <BaseTooltip.Trigger {...props} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          anchor={anchor}
        >
          <BaseTooltip.Popup className={classNames(styles.tooltipPopup, popupClassName)}>
            <BaseTooltip.Arrow className={styles.tooltipArrow} data-visible={arrow === false ? 'hidden' : 'visible'}>
              <IconFont iconName="icon-icon_operation_arrow_up_24" style={{ fontSize: '10.5px' }} />
            </BaseTooltip.Arrow>
            {tooltip}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

export function Tooltip({
  tooltip,
  side,
  align,
  sideOffset,
  alignOffset,
  arrow,
  className,
  popupClassName,
  anchor,
  onOpenChange,
  ...props
}: Omit<TooltipButtonProps, 'className'> & { className?: string }) {
  return (
    <BaseTooltip.Root disabled={!tooltip} onOpenChange={onOpenChange}>
      <BaseTooltip.Trigger className={classNames(styles.tooltipTrigger, className)} {...props} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          anchor={anchor}
        >
          <BaseTooltip.Popup className={classNames(styles.tooltipPopup, popupClassName)}>
            <BaseTooltip.Arrow className={styles.tooltipArrow} data-visible={arrow === false ? 'hidden' : 'visible'}>
              <IconFont iconName="icon-icon_operation_arrow_up_24" style={{ fontSize: '10.5px' }} />
            </BaseTooltip.Arrow>
            {tooltip}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}
